import { useMemo } from 'react';

import type { HeartbeatItem } from '@shared/lib/api/types';
import type { RolloutPayload, RolloutState, RolloutTaskState } from '@shared/lib/api/rollout';

/** The task a live worker is conditioned on, read off its heartbeat. */
export type RolloutTask = {
  name: string | null;
  description: string | null;
  state: RolloutTaskState;
  error: string | null;
  /** Somebody confirmed the prompt on this worker (a set_task succeeded). */
  confirmed: boolean;
  /** null until the worker's engine exists (mid-load). */
  languageConditioned: boolean | null;
  /** The checkpoint's trained instructions; empty when it lists none. */
  instructions: string[];
  /** The instruction the model runs now; null = the task description. */
  instruction: string | null;
  /** The admin pinned `instruction`; the operator dashboard cannot change it. */
  instructionPinned: boolean;
  /** The load predicts sub-task progress. */
  progressTrained: boolean;
  /** Sub-task progress of the last dispatched chunk; null until a progress checkpoint dispatches one. */
  progress: RolloutProgress | null;
  /** The worker's pause_reason; null while it is not paused. */
  pauseReason: string | null;
};

/** The sub-task progress the last dispatched chunk predicted, as the worker posted it. */
export type RolloutProgress = {
  /** At the first row the arms run; not clipped to [0, 1]. */
  value: number;
  /** The whole chunk, one value per action row. */
  chunk: number[];
  /** The text the chunk was conditioned on: the instruction, else the task description. */
  instruction: string | null;
};

export type UseRolloutStateResult = {
  /** rollout_service heartbeat alive (parent RPC server is up). */
  serviceOnline: boolean;
  /** rollout_service_worker heartbeat alive (worker subprocess is up). */
  workerOnline: boolean;
  /**
   * Authoritative "a worker subprocess exists" signal, taken from the SERVICE
   * heartbeat's `worker_alive`. The parent process publishes it and never
   * stalls, so it stays correct while the worker itself is unresponsive.
   * Falls back to the worker heartbeat only for a service payload that
   * predates the flag.
   */
  workerLive: boolean;
  /**
   * Whether the worker heartbeat is FRESH. False does not mean gone: a load
   * holds the GIL well past the 8s dead threshold (checkpoint read, ONNX
   * export, TRT build), so the worker heartbeat flaps to 'dead' mid-load
   * while the subprocess is healthy. Render this as "not responding", never
   * as "idle" — treating it as idle put a Load button back on screen during
   * a load, and the second load_task was rejected with "worker already
   * running".
   */
  workerResponding: boolean;
  servicePayload: RolloutPayload;
  workerPayload: RolloutPayload;
  /** Effective UI state — falls back to 'idle' whenever workerLive is false. */
  rolloutState: RolloutState;
  /** Available checkpoint directories advertised by the rollout service. */
  checkpoints: string[];
  /** Per-checkpoint metadata (resolved .ckpt filename + fetch mtime); may be empty. */
  checkpointMeta: NonNullable<RolloutPayload['checkpoint_meta']>;
  /** Currently-loaded checkpoint directory; undefined when no worker is live. */
  checkpointDir: string | undefined;
  /** run_id of the current/last recorded run; undefined when none/no live worker. */
  runId: string | undefined;
  /** The run records telemetry, so the stop-time outcome prompt has something to label. */
  runTelemetry: boolean;
  /** The task the live worker is conditioned on; null when no worker is live. */
  task: RolloutTask | null;
  /** Service-side error string. */
  errorMessage: string | undefined;
};

/**
 * Derive rollout-policy UI state from the live heartbeat list.
 * Shared by the user task panel and the admin policy page so they agree on
 * what "loaded / ready / idle" means and on how stale heartbeats are handled.
 */
export function useRolloutState(heartbeats: HeartbeatItem[]): UseRolloutStateResult {
  const rolloutHb = useMemo(
    () => heartbeats.find((hb) => hb.heartbeat_id === 'rollout_service'),
    [heartbeats],
  );
  const workerHb = useMemo(
    () => heartbeats.find((hb) => hb.heartbeat_id === 'rollout_service_worker'),
    [heartbeats],
  );

  const serviceOnline = rolloutHb?.status === 'alive';
  const workerOnline = workerHb?.status === 'alive';
  const servicePayload = (rolloutHb?.payload ?? {}) as RolloutPayload;
  const workerPayload = (workerHb?.payload ?? {}) as RolloutPayload;

  // The service heartbeat owns existence; the worker heartbeat owns liveness.
  // state-authority keeps the worker record after the subprocess exits (status
  // flips to 'dead', the last payload sticks), so the service flag is what
  // clears the UI back to idle after an Unload.
  const workerLive = servicePayload.worker_alive ?? workerOnline;
  const workerResponding = workerOnline;
  // Last-known state while the worker is silent: during a load that reads
  // 'loading', which is what the operator needs to see. A worker that spawned
  // but has not published yet has no state at all, and it is loading too.
  const rolloutState: RolloutState = workerLive ? (workerPayload.state ?? 'loading') : 'idle';
  const task: RolloutTask | null = workerLive
    ? {
        name: workerPayload.task_name ?? null,
        description: workerPayload.task_description ?? null,
        state: workerPayload.task_state ?? 'ready',
        error: workerPayload.task_error ?? null,
        confirmed: workerPayload.task_confirmed ?? false,
        languageConditioned: workerPayload.language_conditioned ?? null,
        instructions: workerPayload.instructions ?? [],
        instruction: workerPayload.instruction ?? null,
        instructionPinned: workerPayload.instruction_pinned ?? false,
        progressTrained: workerPayload.subtask_progress_trained ?? false,
        progress:
          workerPayload.subtask_progress != null
            ? {
                value: workerPayload.subtask_progress,
                chunk: workerPayload.subtask_progress_chunk ?? [],
                instruction: workerPayload.subtask_progress_instruction ?? null,
              }
            : null,
        pauseReason: workerPayload.pause_reason ?? null,
      }
    : null;

  return {
    serviceOnline,
    workerOnline,
    workerLive,
    workerResponding,
    servicePayload,
    workerPayload,
    rolloutState,
    checkpoints: servicePayload.available_checkpoints ?? [],
    checkpointMeta: servicePayload.checkpoint_meta ?? {},
    checkpointDir: workerLive ? workerPayload.checkpoint_dir : undefined,
    runId: workerLive ? workerPayload.run_id : undefined,
    runTelemetry: workerLive ? Boolean(workerPayload.run_telemetry) : false,
    task,
    errorMessage: servicePayload.error,
  };
}
